import {
  ArrowLeftOutlined,
  BankOutlined,
  CalendarOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Header, Container } from '../../../../../core/components';
import { JobApplication } from '../models/job-application.model';
import { JobApplicationService } from '../services/job-application.service';
import { UtilService } from '../../../../../core/services/util.service';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useProfile } from '../../profile/components/ProfileContext';
import React, { useEffect, useState, useCallback } from 'react';
import { Spin, Tabs, Tag, Typography } from 'antd';
import JobApplicationSkills from './JobApplicationSkills';
import { CandidateSummaryPanel } from './CandidateSummaryPanel';
import { route } from '../../../../../constants/routes';
import { formatApplicationSkillScore } from '../utils/application-relevance-labels';
import { readJobApplicationsViewMode } from './JobApplicationViewToggle';
import ApplicationWorkspace from './ApplicationWorkspace';
import CandidateSidebar from './CandidateSidebar';
import PipelineRail from './PipelineRail';
import JobDetailsPanel from './JobDetailsPanel';
import CandidateClarificationsPanel from './CandidateClarificationsPanel';
import CandidateMessagesPanel from './CandidateMessagesPanel';
import './JobApplicationWorkspace.css';

function formatCurrency(currency: string | null | undefined): string {
  if (!currency) return '$';
  switch (currency) {
    case 'CRC_COLON':
      return '₡';
    case 'US_DOLLAR':
      return '$';
    default:
      return currency;
  }
}

function currentStageLabel(application: JobApplication): string {
  const stages = application.jobApplicationStatuses || [];
  if (stages.length === 0)
    return application.status?.replace(/_/g, ' ') || 'No stage';
  const latest = stages[stages.length - 1];
  return latest?.statusName?.replace(/_/g, ' ') || 'No stage';
}

function formatApplicationDate(value?: string): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.split('T')[0];
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const JobApplicationForm: React.FC = () => {
  const { companyId } = useProfile();
  const location = useLocation();
  const requestedCenterTab = new URLSearchParams(location.search).get('tab');
  const focusedClarificationId = new URLSearchParams(location.search).get(
    'clarification'
  );
  const focusedMessageId = new URLSearchParams(location.search).get('message');
  const [application, setApplication] = useState<JobApplication>(
    new JobApplication()
  );
  const [statuses, setStatuses] = useState<string[]>([]);
  const [applicationCandidateId, setApplicationCandidateId] =
    useState<string>('');
  const [loading, setLoading] = useState(true);
  const [centerTab, setCenterTab] = useState(() =>
    requestedCenterTab === 'messages'
      ? 'messages'
      : requestedCenterTab === 'clarifications'
        ? 'clarifications'
        : requestedCenterTab === 'brief'
          ? 'brief'
          : 'overview'
  );
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const handleNavigation = useCallback(() => {
    const restoredViewMode = readJobApplicationsViewMode(location.state);

    if (location.state?.fromJobApplicationsByJob && location.state?.jobId) {
      navigate(route(`/job/${location.state.jobId}/job-applications`), {
        state: {
          jobName: location.state.jobName,
          fromJobApplicationsByJob: true,
          jobId: location.state.jobId,
          ...(restoredViewMode
            ? { jobApplicationsViewMode: restoredViewMode }
            : {}),
          ...(location.state?.fromDashboard ? { fromDashboard: true } : {}),
        },
      });
    } else if (location.state?.fromJobApplicationsTable) {
      navigate(route('/job-applications'), {
        state: restoredViewMode
          ? { jobApplicationsViewMode: restoredViewMode }
          : undefined,
      });
    } else if (location.state?.fromDashboard) {
      navigate(route('/dashboard'));
    } else {
      navigate(route('/job-applications'), {
        state: restoredViewMode
          ? { jobApplicationsViewMode: restoredViewMode }
          : undefined,
      });
    }
  }, [location.state, navigate]);

  const GoBack = useCallback(() => {
    handleNavigation();
  }, [handleNavigation]);

  const GetApplications = useCallback(async () => {
    return await JobApplicationService.GetById(id!, (error: Error) => {
      if (error.message.includes('400')) {
        UtilService.Alert(
          'Important!',
          'info',
          'The job applicattion entered does not exist'
        );
        handleNavigation();
      } else {
        UtilService.Alert('Important!', 'info', 'Internal Server Error');
      }
    });
  }, [handleNavigation, id]);

  const fetchJobApplication = useCallback(async () => {
    try {
      setLoading(true);
      const vApplication = await GetApplications();
      if (!vApplication) {
        return;
      }

      let candidateId = '';
      if ((vApplication as any).candidateId) {
        candidateId = (vApplication as any).candidateId;
      } else if (
        vApplication.candidate &&
        typeof vApplication.candidate === 'object'
      ) {
        candidateId = vApplication.candidate.id;
      } else if (typeof vApplication.candidate === 'string') {
        candidateId = vApplication.candidate;
      }

      setApplicationCandidateId(candidateId);

      const nextStatuses =
        vApplication.jobApplicationStatuses?.map(
          (status: any) => status.statusName
        ) || [];

      setApplication(vApplication);
      setStatuses(nextStatuses);
    } catch (error) {
      console.error('Error fetching job application:', error);
    } finally {
      setLoading(false);
    }
  }, [GetApplications]);

  useEffect(() => {
    if (!id) {
      GoBack();
      return;
    }
    void fetchJobApplication();
  }, [GoBack, fetchJobApplication, id]);

  const GetFile = async () => {
    try {
      const blob = await JobApplicationService.GetFile(
        application.id,
        application.file.id
      );
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = application.file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch {
      UtilService.Alert(
        'Error',
        'error',
        'An error occurred while downloading the file.'
      );
    }
  };

  const downloadCandidatePackage = async () => {
    if (!application.id || !application.job?.id) {
      UtilService.Alert(
        'Error',
        'error',
        'The job application is not available for export.'
      );
      return;
    }

    try {
      const blob = await JobApplicationService.ExportApplication(
        application.id,
        application.job.id
      );
      const urlBlob = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = urlBlob;
      anchor.download = `candidate-export-${application.id}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(urlBlob);
      UtilService.Alert(
        'Export ready',
        'success',
        'Candidate package downloaded.'
      );
    } catch {
      UtilService.Alert(
        'Error',
        'error',
        'The candidate package could not be downloaded. Please try again.'
      );
    }
  };

  const handleViewCandidate = () => {
    if (applicationCandidateId) {
      navigate(route(`/candidate-profile/${applicationCandidateId}`), {
        state: {
          fromJobApplication: true,
          jobApplicationId: id,
          candidateName: application.fullname,
          jobName: application.job?.name,
          ...(location.state?.fromDashboard ? { fromDashboard: true } : {}),
        },
      });
    }
  };

  const restoreApplication = async () => {
    if (!application.id) return;
    try {
      await JobApplicationService.Restore(application.id);
      setApplication((current) => ({ ...current, deleted: false }));
      UtilService.Alert(
        'Application restored',
        'success',
        'The application is available in the active applications.'
      );
    } catch (error) {
      console.error('Error restoring application:', error);
      UtilService.Alert(
        'Restore unavailable',
        'error',
        'The application could not be moved back to Active.'
      );
    }
  };

  const candidateSkills = application.skills || [];
  const skillScore = formatApplicationSkillScore(application.relevance);
  const salaryRange =
    application.job?.minSalary || application.job?.maxSalary
      ? `${formatCurrency(application.job?.currency)}${application.job?.minSalary ?? ''} – ${application.job?.maxSalary ?? ''}`
      : 'Not specified';
  const expectedSalary = application.expectedSalary
    ? `${formatCurrency(application.currency || application.job?.currency)}${application.expectedSalary.toLocaleString()}`
    : 'Not specified';
  const appliedDate = formatApplicationDate(
    application.appliedOn || application.createdDate
  );
  const currentStage = currentStageLabel(application);

  const overviewPanel = (
    <div className="application-overview">
      <div className="application-overview__intro">
        <div>
          <Typography.Title level={3} className="!mb-1 !text-lg">
            Application overview
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="!mb-0 text-sm">
            Review the candidate fit and role context before moving them
            forward.
          </Typography.Paragraph>
        </div>
        <Tag color="processing" className="m-0">
          {currentStage}
        </Tag>
      </div>

      <div
        className="application-overview__signals"
        aria-label="Application highlights"
      >
        <div className="application-signal">
          <SafetyCertificateOutlined aria-hidden />
          <span>Skill match</span>
          <strong>{skillScore || 'Not scored'}</strong>
        </div>
        <div className="application-signal">
          <CalendarOutlined aria-hidden />
          <span>Applied</span>
          <strong>{appliedDate}</strong>
        </div>
        <div className="application-signal">
          <FileTextOutlined aria-hidden />
          <span>Resume</span>
          <strong>
            {application.file?.name ? 'Available' : 'Not uploaded'}
          </strong>
        </div>
      </div>

      <section
        className="application-overview__section"
        aria-labelledby="role-context-heading"
      >
        <div className="application-overview__section-heading">
          <Typography.Title
            id="role-context-heading"
            level={4}
            className="!mb-1 !text-base"
          >
            Role context
          </Typography.Title>
          <Typography.Text type="secondary" className="text-xs">
            Candidate expectations compared with the opening
          </Typography.Text>
        </div>
        <dl className="application-facts">
          <div>
            <dt>
              <FileTextOutlined aria-hidden />
              Position
            </dt>
            <dd>{application.job?.name || 'Not specified'}</dd>
          </div>
          <div>
            <dt>
              <BankOutlined aria-hidden />
              Client
            </dt>
            <dd>{application.job?.client?.name || 'Not specified'}</dd>
          </div>
          <div>
            <dt>
              <EnvironmentOutlined aria-hidden />
              Location
            </dt>
            <dd>
              {application.job?.location?.replace(/-/g, ' ') || 'Not specified'}
            </dd>
          </div>
          <div>
            <dt>
              <DollarOutlined aria-hidden />
              Expected salary
            </dt>
            <dd>{expectedSalary}</dd>
          </div>
          <div>
            <dt>
              <DollarOutlined aria-hidden />
              Role salary range
            </dt>
            <dd>{salaryRange}</dd>
          </div>
        </dl>
      </section>

      <div className="application-overview__guidance">
        <SafetyCertificateOutlined aria-hidden />
        <div>
          <strong>Next step</strong>
          <p>
            Review the AI brief and skills, then use the pipeline to add a
            stage, send a notification, or schedule an interview.
          </p>
        </div>
      </div>
    </div>
  );

  const jobPanel = (
    <JobDetailsPanel job={application.job} salaryRange={salaryRange} />
  );

  const centerTabs = [
    {
      key: 'overview',
      label: 'Overview',
      children: overviewPanel,
    },
    ...(application.id
      ? [
          {
            key: 'messages',
            label: 'Messages',
            children: (
              <CandidateMessagesPanel
                applicationId={application.id}
                candidateName={application.fullname}
                jobTitle={application.job?.name || 'Job application'}
                emailAllowed={Boolean(
                  application.emailNotifiable && application.email
                )}
                applicationOpen={!application.closed && !application.deleted}
                focusedMessageId={focusedMessageId ?? undefined}
              />
            ),
          },
        ]
      : []),
    ...(application.job?.featured && application.id
      ? [
          {
            key: 'brief',
            label: 'AI brief',
            children: (
              <CandidateSummaryPanel
                applicationId={application.id}
                resumeProcessing={application.visible === false}
                candidateName={application.fullname}
                jobTitle={application.job?.name}
                companyName={application.job?.company}
              />
            ),
          },
        ]
      : []),
    ...(application.id
      ? [
          {
            key: 'clarifications',
            label: 'Clarifications',
            children: (
              <CandidateClarificationsPanel
                applicationId={application.id}
                candidateName={application.fullname}
                jobTitle={application.job?.name || 'Job application'}
                emailAllowed={application.emailNotifiable}
                whatsappAllowed={application.whatsappNotifiable}
                focusedRequestId={focusedClarificationId ?? undefined}
              />
            ),
          },
        ]
      : []),
    {
      key: 'skills',
      label: 'Skills',
      children: (
        <JobApplicationSkills
          skills={candidateSkills}
          jobSkills={application.job?.skills || []}
          score={application.relevance}
        />
      ),
    },
    {
      key: 'job',
      label: 'Job details',
      children: jobPanel,
    },
  ];

  const workspaceHeader = (
    <Header
      title={application.fullname || 'Job Application'}
      subtitle={
        application.job?.name
          ? `${application.job.name}${application.job?.client?.name ? ` · ${application.job.client.name}` : ''}`
          : 'Job Application'
      }
      buttons={[
        {
          icon: { value: <ArrowLeftOutlined />, size: 'text-lg' },
          text: { size: 'md', value: 'Back', weight: 500 },
          color: 'gray',
          tone: 600,
          onClick: GoBack,
        },
        ...(application.deleted
          ? [
              {
                icon: { value: <RollbackOutlined />, size: 'text-lg' },
                text: {
                  size: 'md',
                  value: 'Move to active',
                  weight: 500 as const,
                },
                color: 'blue' as const,
                tone: 600 as const,
                onClick: () => void restoreApplication(),
              },
            ]
          : []),
      ]}
    />
  );

  return (
    <div className="mx-auto">
      <Container>
        {loading && !application.id ? (
          <div
            className="flex min-h-[320px] items-center justify-center"
            role="status"
          >
            <Spin size="large" tip="Loading application…" />
          </div>
        ) : (
          <ApplicationWorkspace
            header={workspaceHeader}
            sidebar={
              <CandidateSidebar
                application={application}
                onViewCandidate={handleViewCandidate}
                onDownloadResume={GetFile}
                onDownloadPackage={downloadCandidatePackage}
              />
            }
            main={
              <Tabs
                activeKey={centerTab}
                onChange={setCenterTab}
                items={centerTabs}
                destroyInactiveTabPane={false}
                className="application-center-tabs"
              />
            }
            pipeline={
              <PipelineRail
                application={application}
                statuses={statuses}
                companyId={companyId || undefined}
                onStageChanged={fetchJobApplication}
              />
            }
          />
        )}
      </Container>
    </div>
  );
};

export default JobApplicationForm;
